import React, { useContext, useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { AlertContainer, FormColumn, StyledAlert, StyledCard } from './create_access_requests.styles'
import { ContentContainer, MainContainer, CardContainer } from '../focus_view_shared.styles'
import { InputFormGroup, Text } from '@amzn/storm-ui'
import { getMetricsPublisherAsync, getMetricsPublisherChainAsync, publishCounter } from '@amzn/aapn-front-end-metrics'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../util/app_initialization/appconfig'
import { ControlColumn, StyledInputLabel } from '../../manage_shared.styles'
import { isEmailAddress } from '@amzn/aapn-type-guards'
import { useSendPartnerAdvertiserInviteMutation } from '../../../../../generated/graphql'
import { PARTNER_ID_HEADER_KEY } from '../../../../../util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { GraphQLError } from 'graphql'
import { toNullable } from 'fp-ts/es6/Option'
import { useLoginContext } from 'stagedive/LoginContext'
import * as i18nKeyList from './create_access_request_18n_mappings'
import {
  CREATE_ACCESS_REQUEST_LOADING_BUTTON_LABEL,
  CREATE_ACCESS_REQUEST_SEND_BUTTON_LABEL,
  CREATE_ACCESS_REQUEST_SUCCESS_ALERT,
} from './create_access_request_18n_mappings'
import { getManageAccountsURL } from '../ads_console_utils'
import * as AACChromeAPI from '@amzn/aac-chrome-api'

export type SendPartnerAdvertiserInviteResult = 'success' | 'error' | null
export const CreateAccessRequest: React.FunctionComponent = (): JSX.Element => {
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)
  const { t } = useTranslation()
  const [emailAddressToRequest, setEmailAddressToRequest] = useState('')
  const [lastEnteredEmailAddressInvite, setLastEnteredEmailAddressInvite] = useState('')
  const [sendInvite, { loading: sendLoading }] = useSendPartnerAdvertiserInviteMutation()
  const [sendPartnerAdvertiserInviteResult, setSendPartnerAdvertiserInviteResult] = useState<SendPartnerAdvertiserInviteResult>(null)
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''

  useEffect(() => {
    getMetricsPublisherChainAsync('AdsConsoleInt').then((p) => p.publishCounter('view', 1).publishString(`page`, `create-access-request`))
  }, [])

  /* istanbul ignore next */
  useEffect(() => {
    const config = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    config.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const button: AACChromeAPI.BUTTON = new AACChromeAPI.Button()
    const sendButtonId = 'sendButtonId'
    button.setId(sendButtonId)

    button.setLabel(sendLoading ? t(CREATE_ACCESS_REQUEST_LOADING_BUTTON_LABEL) : t(CREATE_ACCESS_REQUEST_SEND_BUTTON_LABEL))
    button.setButtonType(isEmailAddress(emailAddressToRequest.trim()) ? AACChromeAPI.ButtonType.PRIMARY : AACChromeAPI.ButtonType.NORMAL)
    button.setState(
      sendLoading
        ? AACChromeAPI.ActionState.LOADING
        : isEmailAddress(emailAddressToRequest.trim())
        ? AACChromeAPI.ActionState.ENABLED
        : AACChromeAPI.ActionState.DISABLED,
    )
    const buttonClickFn = (): void => {
      return onClickSendInvite()
    }
    AACChromeAPI.getActionEventTarget().addEventListener(sendButtonId, buttonClickFn)

    const backToAccountsLink = new AACChromeAPI.Button()
    const backToAccountsLinkButtonId = 'backToAccountsLink'
    backToAccountsLink.setId(backToAccountsLinkButtonId)

    backToAccountsLink.setLabel(t(i18nKeyList.CREATE_ACCESS_REQUEST_BACK_TO_ACCOUNTS))
    backToAccountsLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const backToAccountsLinkFn = (): void => {
      window.location.href = getManageAccountsURL(partnerAccountId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getActionEventTarget().addEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToAccountsLinkFn)

    actionList.addItem(backToAccountsLink)
    actionList.addItem(button)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(i18nKeyList.CREATE_ACCESS_REQUEST_PAGE_TITLE))
    config.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(config)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(sendButtonId, buttonClickFn)
      AACChromeAPI.getActionEventTarget().removeEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
      AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToAccountsLinkFn)
    }
  }, [sendLoading, emailAddressToRequest, t])
  const onClickSendInvite = (): void => {
    setSendPartnerAdvertiserInviteResult(null)
    setLastEnteredEmailAddressInvite(emailAddressToRequest)
    sendInvite({
      variables: {
        email: emailAddressToRequest.trim(),
      },
      context: {
        headers: {
          [PARTNER_ID_HEADER_KEY]: partnerAccountId,
        },
      },
    })
      .then(() => {
        setSendPartnerAdvertiserInviteResult('success')
        getMetricsPublisherAsync('CreateAccessRequest').then((p) => {
          publishCounter('CreateAccessRequest.success', 1)(p)
        })
        setEmailAddressToRequest('')
      })
      .catch((_: GraphQLError) => {
        getMetricsPublisherAsync('CreateAccessRequest').then((p) => {
          publishCounter('CreateAccessRequest.fail', 1)(p)
        })
        setSendPartnerAdvertiserInviteResult('error')
      })
  }

  return (
    <MainContainer>
      <ContentContainer>
        <AlertContainer>
          <StyledAlert
            type={sendPartnerAdvertiserInviteResult === 'success' ? 'success' : 'error'}
            isOpen={!!sendPartnerAdvertiserInviteResult}
            withCloseButton={true}
            onClose={() => setSendPartnerAdvertiserInviteResult(null)}
          >
            <Text>
              {sendPartnerAdvertiserInviteResult === 'success' ? (
                <Trans i18nKey={CREATE_ACCESS_REQUEST_SUCCESS_ALERT} values={{ email: lastEnteredEmailAddressInvite.trim() }} />
              ) : (
                t(i18nKeyList.CREATE_ACCESS_REQUEST_ERROR_ALERT)
              )}
            </Text>
          </StyledAlert>
        </AlertContainer>
        <CardContainer>
          <StyledCard>
            <Text>{t(i18nKeyList.CREATE_ACCESS_REQUEST_CARD_DESCRIPTION)}</Text>
            <FormColumn>
              <StyledInputLabel htmlFor="input-email">{t(i18nKeyList.CREATE_ACCESS_REQUEST_EMAIL_LABEL)}</StyledInputLabel>
              <ControlColumn>
                <InputFormGroup
                  id="input-email"
                  value={emailAddressToRequest}
                  placeholder={t(i18nKeyList.CREATE_ACCESS_REQUEST_EMAIL_PLACEHOLDER)}
                  onChange={(e: React.FormEvent<HTMLInputElement>) => setEmailAddressToRequest(e.currentTarget.value)}
                />
              </ControlColumn>
            </FormColumn>
          </StyledCard>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  )
}
